import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { PenTool, Check, Loader2, Clock, AlertTriangle, X, RotateCcw, Pen } from 'lucide-react';

// This page is PUBLIC — no JWT header. Build a bare axios client.
const BACKEND = process.env.REACT_APP_BACKEND_URL || '';
const publicApi = axios.create({ baseURL: `${BACKEND}/api` });

export default function SignaturePage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [signing, setSigning] = useState(false);
  const [signatureName, setSignatureName] = useState('');
  // Multi-field signatures keyed by field_id (for PDF-layout signing)
  const [fieldSignatures, setFieldSignatures] = useState({});
  const [activeSignField, setActiveSignField] = useState(null);
  // Legacy single-canvas state (non-PDF forms)
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const lastRef = useRef(null);
  const [hasDrawn, setHasDrawn] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await publicApi.get(`/sign/${token}`);
        setData(res.data);
        setSignatureName(res.data?.signer_name || res.data?.doctor_name || '');
      } catch { setData({ status: 'error' }); }
      finally { setLoading(false); }
    })();
  }, [token]);

  // --- Legacy single-canvas handlers ---
  const getPoint = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const touch = e.touches?.[0] || e.changedTouches?.[0];
    const cx = touch ? touch.clientX : e.clientX;
    const cy = touch ? touch.clientY : e.clientY;
    return { x: (cx - rect.left), y: (cy - rect.top) };
  };
  const startDraw = (e) => { e.preventDefault?.(); drawingRef.current = true; lastRef.current = getPoint(e); };
  const draw = (e) => {
    if (!drawingRef.current) return;
    e.preventDefault?.();
    const ctx = canvasRef.current.getContext('2d');
    const p = getPoint(e);
    ctx.beginPath(); ctx.moveTo(lastRef.current.x, lastRef.current.y); ctx.lineTo(p.x, p.y);
    ctx.lineWidth = 2.5; ctx.lineCap = 'round'; ctx.strokeStyle = '#0A2342'; ctx.stroke();
    lastRef.current = p;
    if (!hasDrawn) setHasDrawn(true);
  };
  const endDraw = () => { drawingRef.current = false; };
  const clearCanvas = () => {
    const c = canvasRef.current; if (!c) return;
    c.getContext('2d').clearRect(0, 0, c.width, c.height);
    setHasDrawn(false);
  };

  const handleLegacySubmit = async () => {
    if (!hasDrawn) { toast.error('Please sign first'); return; }
    if (!signatureName.trim()) { toast.error('Please type your name'); return; }
    setSigning(true);
    try {
      const dataUrl = canvasRef.current.toDataURL('image/png');
      await publicApi.post(`/sign/${token}`, { name: signatureName.trim(), signature: dataUrl });
      setData(prev => ({ ...prev, status: 'already_signed', signed_at: new Date().toISOString() }));
      toast.success('Signature submitted. Thank you!');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Submit failed');
    } finally { setSigning(false); }
  };

  const handleMultiSubmit = async () => {
    const allowed = data.signature_fields || [];
    const missing = allowed.filter(fid => !fieldSignatures[fid]);
    if (missing.length) { toast.error(`Please sign all ${allowed.length} signature fields`); return; }
    if (!signatureName.trim()) { toast.error('Please type your full name'); return; }
    setSigning(true);
    try {
      await publicApi.post(`/sign/${token}`, { name: signatureName.trim(), signatures: fieldSignatures });
      setData(prev => ({ ...prev, status: 'already_signed', signed_at: new Date().toISOString() }));
      toast.success('All signatures submitted. Thank you!');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Submit failed');
    } finally { setSigning(false); }
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen"><Loader2 className="w-10 h-10 animate-spin text-[#0E7490]" /></div>;
  }
  if (!data || data.status === 'error') return <StatusCard icon={AlertTriangle} tone="bad" title="Link not found" desc="This signing link is invalid or has been removed." />;
  if (data.status === 'expired') return <StatusCard icon={Clock} tone="warn" title="Link expired" desc="This signing link has expired. Please request a new one." />;
  if (data.status === 'already_signed') return <StatusCard icon={Check} tone="good" title="Signed" desc={`Signed on ${new Date(data.signed_at).toLocaleString()}. You may safely close this window.`} />;

  const isPdfLayout = data.print_layout === 'pdf_original' && (data.source_pdf_pages || []).length > 0;
  const signerTypeLabel = (data.signer_type === 'patient') ? 'Patient' : (data.signer_type === 'physician') ? 'Physician' : 'Signer';

  return (
    <div className="min-h-screen bg-[#F1F5F9] pb-12" data-testid="e-sign-page">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-white border-b border-[#E2E8F0]">
        <div className="max-w-5xl mx-auto p-4 flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-lg md:text-xl font-semibold text-[#0A2342]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Electronic Signature — {data.form_name || 'Form'}
            </h1>
            <p className="text-xs text-[#64748B]">
              Signing as <span className="font-medium text-[#0A2342]">{data.signer_name || signerTypeLabel}</span>
              {data.patient_name && ` · Patient: ${data.patient_name}`}
              {data.requested_by_name && ` · Requested by ${data.requested_by_name}`}
            </p>
          </div>
          <div className="text-[10px] text-[#64748B] text-right">
            <div>Secure, one-time link</div>
            {data.expires_at && <div>Expires {new Date(data.expires_at).toLocaleString()}</div>}
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto p-3 md:p-6 space-y-4">
        {isPdfLayout ? (
          <PdfLayoutSigner
            data={data}
            fieldSignatures={fieldSignatures}
            onOpenSignModal={setActiveSignField}
          />
        ) : (
          <LegacySimpleSigner
            data={data}
            canvasRef={canvasRef}
            startDraw={startDraw}
            draw={draw}
            endDraw={endDraw}
            clearCanvas={clearCanvas}
          />
        )}

        {/* Legal + name input */}
        <Card className="careiq-card">
          <CardContent className="p-4 space-y-3">
            <div className="text-xs text-[#64748B] bg-[#FEF3C7]/40 p-3 rounded border border-[#CA8A04]/30">
              By signing electronically, you agree this is the legal equivalent of a handwritten signature.
              Your signature, name, IP address, and timestamp are captured for audit purposes under HIPAA (45 CFR § 164.312).
            </div>
            <div>
              <Label className="careiq-label">Type your full legal name *</Label>
              <Input
                value={signatureName}
                onChange={e => setSignatureName(e.target.value)}
                placeholder="e.g., Jane Doe"
                className="careiq-input"
                data-testid="e-sign-name-input"
              />
            </div>
            <Button
              className="careiq-btn-primary w-full flex items-center justify-center gap-2 text-base py-6"
              onClick={isPdfLayout ? handleMultiSubmit : handleLegacySubmit}
              disabled={signing}
              data-testid="e-sign-submit-btn"
            >
              {signing ? <Loader2 className="w-5 h-5 animate-spin" /> : <Check className="w-5 h-5" />}
              {signing ? 'Submitting...' : 'Submit Signatures'}
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Active signature modal for PDF-layout mode */}
      {activeSignField && (
        <SignatureModal
          field={activeSignField}
          initialValue={fieldSignatures[activeSignField.field_id || activeSignField.id]}
          onClose={() => setActiveSignField(null)}
          onSave={(dataUrl) => {
            setFieldSignatures(prev => ({ ...prev, [activeSignField.field_id || activeSignField.id]: dataUrl }));
            setActiveSignField(null);
          }}
        />
      )}
    </div>
  );
}

// ---------- PDF-layout signer: shows PDF w/ pre-filled values + tappable sig fields ----------
function PdfLayoutSigner({ data, fieldSignatures, onOpenSignModal }) {
  const responses = data.responses || {};
  const allowed = new Set(data.signature_fields || []);
  return (
    <div className="space-y-4" data-testid="pdf-layout-signer">
      <div className="text-xs text-[#64748B] p-3 rounded bg-[#F0FDFA] border border-[#A7F3D0]">
        Review the pre-filled form below. Tap each highlighted box to draw your signature.
        You need to sign <span className="font-semibold text-[#065F46]">{(data.signature_fields || []).length}</span> field(s).
      </div>
      {(data.source_pdf_pages || []).map((pageB64, pageIdx) => {
        const fieldsOnPage = (data.form_fields || []).filter(f => (f.page_index ?? 0) === pageIdx);
        return (
          <div key={pageIdx} className="relative bg-white shadow-md mx-auto rounded-md overflow-hidden" style={{ maxWidth: '850px' }}>
            <div className="relative">
              <img src={`data:image/png;base64,${pageB64}`} alt="Form" className="block w-full h-auto select-none" draggable={false} />
              {fieldsOnPage.map(f => (
                <FieldDisplay
                  key={f.field_id || f.id}
                  field={f}
                  value={fieldSignatures[f.field_id || f.id] || responses[f.field_id || f.id]}
                  hasInlineSignature={!!fieldSignatures[f.field_id || f.id]}
                  mySignatureField={allowed.has(f.field_id || f.id)}
                  onOpenSignModal={() => onOpenSignModal(f)}
                />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function FieldDisplay({ field, value, hasInlineSignature, mySignatureField, onOpenSignModal }) {
  const bbox = field.bbox || {};
  const fieldType = field.field_type || field.type || 'text';
  const style = {
    position: 'absolute',
    left: `${(bbox.x || 0) * 100}%`,
    top: `${(bbox.y || 0) * 100}%`,
    width: `${Math.max((bbox.w || 0.1), 0.04) * 100}%`,
    minHeight: `${Math.max(bbox.h || 0.025, 0.025) * 100}%`,
  };

  // Signature field I'm allowed to sign — interactive
  if (fieldType === 'signature' && mySignatureField) {
    return (
      <button
        type="button"
        onClick={onOpenSignModal}
        className={`flex items-center justify-center rounded transition-colors ${hasInlineSignature ? 'bg-[#F0FDFA] ring-2 ring-[#0E7490]' : 'bg-[#FEF3C7]/70 hover:bg-[#FEF3C7] ring-2 ring-[#CA8A04] animate-pulse'}`}
        style={style}
        data-testid={`e-sign-field-${field.field_id || field.id}`}
      >
        {hasInlineSignature ? (
          <img src={value} alt="Signature" className="max-h-full max-w-full object-contain" />
        ) : (
          <div className="flex items-center gap-1 text-[10px] text-[#92400E] font-medium px-1">
            <Pen className="w-3 h-3" />
            <span>Tap to sign</span>
          </div>
        )}
      </button>
    );
  }

  // Pre-filled values (read-only overlay) — only render when there's a value
  if (value === undefined || value === null || value === '') return null;
  const cursiveStyle = { ...style, fontFamily: 'Caveat, "Segoe Print", cursive', fontSize: 'clamp(9px, 1.2vw, 14px)', color: '#0A2342', paddingLeft: '2px', pointerEvents: 'none', whiteSpace: 'pre-wrap', wordBreak: 'break-word' };

  if (fieldType === 'signature' && String(value).startsWith('data:image/')) {
    return <img src={value} alt="Signature" style={{ ...style, objectFit: 'contain', pointerEvents: 'none' }} />;
  }
  if (fieldType === 'checkbox') {
    const checked = value === true || value === 'true' || value === 'yes';
    if (!checked) return null;
    return <div style={{ ...cursiveStyle, fontSize: '16px', fontWeight: 700, fontFamily: 'Arial, sans-serif' }}>✓</div>;
  }
  const display = Array.isArray(value) ? value.join(', ') : String(value);
  return <div style={cursiveStyle}>{display}</div>;
}

// ---------- Legacy simple signer (for non-PDF-layout forms) ----------
function LegacySimpleSigner({ data, canvasRef, startDraw, draw, endDraw, clearCanvas }) {
  return (
    <Card className="careiq-card">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2 text-[#0A2342]">
          <PenTool className="w-5 h-5 text-[#0E7490]" />
          Draw your signature
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Summary of pre-filled fields (optional preview) */}
        {data.resolved_responses && Object.keys(data.resolved_responses).length > 0 && (
          <details className="text-xs text-[#64748B] border rounded p-2">
            <summary className="cursor-pointer font-medium">Preview form responses ({Object.keys(data.resolved_responses).length} fields)</summary>
            <div className="mt-2 space-y-1">
              {(data.form_fields || []).slice(0, 15).map(f => (
                <div key={f.field_id} className="flex gap-2">
                  <span className="font-medium text-[#0A2342] min-w-[150px] truncate">{f.label}:</span>
                  <span className="truncate">{String(data.resolved_responses[f.field_id] ?? '—')}</span>
                </div>
              ))}
            </div>
          </details>
        )}
        <div className="border-2 border-dashed border-[#CBD5E1] rounded-md bg-[#F8FAFC]" style={{ height: '220px' }}>
          <canvas
            ref={canvasRef}
            width={800} height={220}
            onMouseDown={startDraw} onMouseMove={draw} onMouseUp={endDraw} onMouseLeave={endDraw}
            onTouchStart={startDraw} onTouchMove={draw} onTouchEnd={endDraw}
            style={{ width: '100%', height: '100%', touchAction: 'none', cursor: 'crosshair' }}
            data-testid="e-sign-canvas"
          />
        </div>
        <Button variant="outline" size="sm" onClick={clearCanvas} className="flex items-center gap-1">
          <RotateCcw className="w-3 h-3" /> Clear
        </Button>
      </CardContent>
    </Card>
  );
}

// ---------- Status card (expired/error/signed) ----------
function StatusCard({ icon: Icon, tone, title, desc }) {
  const toneClass = tone === 'good' ? 'text-[#166534] bg-[#F0FDFA] border-[#A7F3D0]' : tone === 'warn' ? 'text-[#92400E] bg-[#FEF3C7] border-[#FCD34D]' : 'text-[#991B1B] bg-[#FEE2E2] border-[#FCA5A5]';
  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-[#F1F5F9]">
      <div className={`max-w-md w-full p-8 rounded-xl border ${toneClass} text-center`}>
        <Icon className="w-12 h-12 mx-auto mb-3" />
        <h2 className="text-xl font-semibold mb-2">{title}</h2>
        <p className="text-sm">{desc}</p>
      </div>
    </div>
  );
}

// ---------- Signature canvas modal (reused) ----------
function SignatureModal({ field, initialValue, onClose, onSave }) {
  const canvasRef = useRef(null);
  const [hasInk, setHasInk] = useState(!!initialValue);
  const drawingRef = useRef(false);
  const lastRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    canvas.width = Math.floor(rect.width * (window.devicePixelRatio || 1));
    canvas.height = Math.floor(rect.height * (window.devicePixelRatio || 1));
    const ctx = canvas.getContext('2d');
    ctx.scale(window.devicePixelRatio || 1, window.devicePixelRatio || 1);
    ctx.lineWidth = 2.5; ctx.lineCap = 'round'; ctx.strokeStyle = '#0A2342';
    if (initialValue) {
      const img = new Image();
      img.onload = () => ctx.drawImage(img, 0, 0, rect.width, rect.height);
      img.src = initialValue;
    }
  }, [initialValue]);

  const pt = (e) => {
    const c = canvasRef.current; const r = c.getBoundingClientRect();
    return { x: (e.touches?.[0]?.clientX ?? e.clientX) - r.left, y: (e.touches?.[0]?.clientY ?? e.clientY) - r.top };
  };
  const start = (e) => { e.preventDefault(); drawingRef.current = true; lastRef.current = pt(e); };
  const move = useCallback((e) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const p = pt(e);
    ctx.beginPath(); ctx.moveTo(lastRef.current.x, lastRef.current.y); ctx.lineTo(p.x, p.y); ctx.stroke();
    lastRef.current = p;
    if (!hasInk) setHasInk(true);
  }, [hasInk]);
  const end = () => { drawingRef.current = false; };
  const clear = () => { const c = canvasRef.current; c.getContext('2d').clearRect(0, 0, c.width, c.height); setHasInk(false); };
  const save = () => { if (!hasInk) { toast.error('Draw your signature'); return; } onSave(canvasRef.current.toDataURL('image/png')); };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-3" data-testid="e-sign-modal">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl p-4 md:p-6">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-sm font-semibold text-[#0A2342]">Sign: {field.label}</div>
            <div className="text-[11px] text-[#64748B]">Use your finger or stylus to draw</div>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}><X className="w-4 h-4" /></Button>
        </div>
        <div className="border-2 border-dashed border-[#CBD5E1] rounded-md bg-[#F8FAFC]" style={{ height: '220px' }}>
          <canvas ref={canvasRef} onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end} onTouchStart={start} onTouchMove={move} onTouchEnd={end} style={{ width: '100%', height: '100%', touchAction: 'none', cursor: 'crosshair' }} />
        </div>
        <div className="flex items-center justify-between mt-4 gap-2 flex-wrap">
          <Button variant="outline" onClick={clear}><RotateCcw className="w-4 h-4 mr-1" /> Clear</Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button className="careiq-btn-primary" onClick={save} disabled={!hasInk}><Check className="w-4 h-4 mr-1" /> Save</Button>
          </div>
        </div>
      </div>
    </div>
  );
}
